/* SafeHaven live layer (shb-live.js) - defer-safe, no dependencies.
   A) .sh-js bootstrap + body.sh2-home on the index template.
   B) ONE IntersectionObserver: .sh-reveal -> .is-in, unobserve after fire.
   C) Hub orbit: pointerenter/leave lights the matching spoke (.is-lit).
   D) ONE rAF loop: scroll progress -> inline scaleY on the thread fills.
   E) Theme-editor rewiring on shopify:section:load/unload (editor-only
      events; live-page behavior is unchanged, same IO + same loop).
   F) ONE-SHOT Discord presence fill: idle fetch of the invite endpoint,
      writes into [data-sh-disc]; any failure leaves it hidden.
   Budget: exactly one IO + at most one rAF loop sitewide. */
(function () {
  "use strict";
  if (window.__sh2live) return;
  window.__sh2live = 1;
  var d = document;
  d.documentElement.classList.add("sh-js");

  var io = null, fills = [], dirty = true, last = -1;
  var setHeadH = function () {}, measure = function () {};

  /* B-scope: observe .sh-reveal inside root (or show at once w/o IO) */
  function observeScope(root) {
    var els = root.querySelectorAll(".sh-reveal"), i;
    for (i = 0; i < els.length; i++) {
      if (io) io.observe(els[i]);
      else els[i].classList.add("is-in");
    }
  }

  /* C-scope: spoke highlight wiring inside root (verbatim behavior) */
  function bindSpokes(root) {
    var nodes = root.querySelectorAll(".sh-hub [data-th]"), i;
    for (i = 0; i < nodes.length; i++) (function (n) {
      if (n.classList.contains("sh-spoke")) return;
      var sec = n.closest ? n.closest(".sh-hub") : null;
      var spoke = sec ? sec.querySelector('.sh-spoke[data-th="' + n.getAttribute("data-th") + '"]') : null;
      if (!spoke) return;
      n.addEventListener("pointerenter", function () { spoke.classList.add("is-lit"); });
      n.addEventListener("pointerleave", function () { spoke.classList.remove("is-lit"); });
    })(nodes[i]);
  }

  /* D-scope: re-query fills; last=-1 forces a rewrite on the next frame */
  function refreshFills() {
    fills = d.querySelectorAll(".sh2-thread-fill");
    dirty = true;
    last = -1;
  }

  /* F-scope: one-shot presence fill. No observers, no loops - a single
     idle-time fetch. On ANY failure (network, CSP, shape, count < 1)
     nothing happens and the pips stay purely decorative. */
  var discN = 0;
  function discApply() {
    var el = d.querySelector("[data-sh-disc]");
    if (!el || discN < 1) return;
    var t = el.querySelector("[data-sh-disc-text]");
    if (!t) return;
    t.textContent = discN + " " + (el.getAttribute("data-sh-disc-suffix") || "in the lounge right now");
    el.hidden = false;
  }
  function discFetch() {
    if (!window.fetch || !d.querySelector("[data-sh-disc]")) return;
    var run = function () {
      try {
        window.fetch("https://discord.com/api/v10/invites/4JAaZ3KEQB?with_counts=true")
          .then(function (r) { if (!r.ok) throw 0; return r.json(); })
          .then(function (j) {
            var n = j && j.approximate_presence_count;
            if (typeof n === "number" && isFinite(n) && n >= 1) {
              discN = Math.round(n);
              discApply();
            }
          })["catch"](function () {});
      } catch (err) {}
    };
    if ("requestIdleCallback" in window) window.requestIdleCallback(run, { timeout: 4000 });
    else window.setTimeout(run, 1500);
  }

  function init() {
    /* A) home-template scope class for shb-7/8 page-wide styling */
    var tpl = (window.SafeHaven && window.SafeHaven.template) || "";
    if (tpl === "index" && d.body) d.body.classList.add("sh2-home");

    /* A2) header offset -> --sh2-headh, so 100svh sections respect the
       non-sticky header instead of pushing content below the fold. */
    var de = d.documentElement, mc = d.getElementById("MainContent");
    setHeadH = function () {
      if (!mc) return;
      var t = Math.max(0, Math.round(mc.getBoundingClientRect().top + (window.pageYOffset || 0)));
      de.style.setProperty("--sh2-headh", t + "px");
    };
    setHeadH();
    window.addEventListener("resize", setHeadH, { passive: true });

    /* B) the one IntersectionObserver */
    if ("IntersectionObserver" in window) {
      io = new IntersectionObserver(function (entries) {
        for (var j = 0; j < entries.length; j++) {
          if (entries[j].isIntersecting) {
            entries[j].target.classList.add("is-in");
            io.unobserve(entries[j].target);
          }
        }
      }, { threshold: 0.15, rootMargin: "0px 0px -8% 0px" });
    }
    observeScope(d);
    bindSpokes(d);
    discFetch();

    /* D) the one rAF loop - thread fills exist only on the homepage, so
       on every other template the loop and its listeners never start.
       Inline transforms, never a root CSS var (that was the jank). */
    var mm = window.matchMedia && window.matchMedia("(prefers-reduced-motion: no-preference)");
    fills = d.querySelectorAll(".sh2-thread-fill");
    if (fills.length && mm && mm.matches && "requestAnimationFrame" in window) {
      var max = 1;
      measure = function () {
        max = Math.max(1, de.scrollHeight - window.innerHeight);
        dirty = true;
      };
      measure();
      window.addEventListener("scroll", function () { dirty = true; }, { passive: true });
      window.addEventListener("resize", measure, { passive: true });
      var loop = function () {
        if (dirty) {
          dirty = false;
          var v = (window.pageYOffset || de.scrollTop || 0) / max;
          if (v < 0) v = 0;
          if (v > 1) v = 1;
          if (Math.abs(v - last) > 0.002) {
            last = v;
            var t = "scaleY(" + v.toFixed(4) + ")";
            for (var k = 0; k < fills.length; k++) fills[k].style.transform = t;
          }
        }
        window.requestAnimationFrame(loop);
      };
      window.requestAnimationFrame(loop);
    }

    /* E) editor-only: re-renders replace section DOM without a resize */
    d.addEventListener("shopify:section:load", function (e) {
      observeScope(e.target || d);
      bindSpokes(e.target || d);
      refreshFills();
      setHeadH();
      measure();
      discApply();
    });
    d.addEventListener("shopify:section:unload", function () {
      refreshFills();
      setHeadH();
      measure();
    });
  }

  if (d.readyState === "loading") d.addEventListener("DOMContentLoaded", init);
  else init();
})();
